/** Mirrors `GlobalDialpad`'s `micPermission` state shape. */
type MicPermissionState = 'checking' | 'granted' | 'denied' | 'unsupported';

export interface CallButtonGateState {
  phoneNumber: string;
  isCreatingCall: boolean;
  isStarting: boolean;
  micPermission: MicPermissionState;
  /** `useProgramPhoneNumbers(...).isLoading` — whether the registry read has settled. */
  numbersLoading: boolean;
}

/**
 * Whether GlobalDialpad's Call button should be disabled.
 *
 * `numbersLoading` closes the same gap the mic gate already closes for
 * `micPermission === 'checking'`: a dial started before an async source
 * resolves can fail after the call record already exists. The registry read
 * carries the identical risk — the mic probe resolving before the react-query
 * fetch settles is the ordinary case (an already-granted permission returns
 * fast), and without this gate `providers` is briefly `[]`, `activeProvider` is
 * `undefined`, and `pickCallerId` returns `undefined` — presenting "no numbers
 * configured" for a program that has plenty.
 */
export function isCallButtonDisabled({
  phoneNumber,
  isCreatingCall,
  isStarting,
  micPermission,
  numbersLoading,
}: CallButtonGateState): boolean {
  return (
    !phoneNumber || isCreatingCall || isStarting || micPermission !== 'granted' || numbersLoading
  );
}

export interface NoiseCancellationToggleState {
  /** The proxied (IVR Assist) path is on: it runs Krisp inside its own mic graph. */
  proxied: boolean;
  /** `voiceService().supportsNoiseCancellation()` for the carrier serving the call. */
  providerSupportsIt: boolean;
}

/**
 * Whether GlobalDialpad shows the noise-cancellation toggle during a call.
 *
 * A provider that cannot cancel noise throws when toggled, so offering the
 * button there only produces an error toast.
 */
export function shouldShowNoiseCancellationToggle({
  proxied,
  providerSupportsIt,
}: NoiseCancellationToggleState): boolean {
  return proxied || providerSupportsIt;
}
